import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Check, Search, Shield, ChevronDown } from "lucide-react";
import {
  Button, Checkbox,
  Dialog, DialogContent,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import { useCreateAccessGroup, useCreateAccessGroupsBulk } from "@/features/identity-hub/hooks/useIdentityHub";
import { usePermissions } from "@/features/auth/hooks/usePermissions";
import { OrgMultiPicker } from "./OrgMultiPicker";
import type { OrgPickerItem } from "./OrgPickerCombobox";
import { DialogIconHeader } from "./shared/dialog/DialogIconHeader";
import {
  FEATURE_AREAS,
  PRESET_DEFS,
  ROLE_PERMISSION_DIALOG_CONTENT_CLASS,
  TOTAL_PERM_COUNT,
  featureAreaKeys,
  type FeatureArea,
  type PermDef,
} from "@/features/identity-hub/lib/permission-catalog";
import { permissionKeysToPayload, usePermissionSelection } from "@/features/identity-hub/hooks/usePermissionSelection";

export type { PermDef } from "@/features/identity-hub/lib/permission-catalog";

// ─── Feature area section ─────────────────────────────────────────────────────

function areaHasVisiblePerms(area: FeatureArea, matchesPerm: (perm: PermDef) => boolean): boolean {
  if (area.pagePerm && matchesPerm(area.pagePerm)) return true;
  if ((area.actionGroups ?? []).some((group) => group.perms.some(matchesPerm))) return true;
  return (area.children ?? []).some((child) => areaHasVisiblePerms(child, matchesPerm));
}

export function FeatureAreaSection({
  area,
  selected,
  onTogglePerm,
  onToggleGroup,
  onRemoveKeys,
  filter,
  showSelectedOnly,
  defaultExpanded,
}: {
  area: FeatureArea;
  selected: Set<string>;
  onTogglePerm: (key: string) => void;
  onToggleGroup: (keys: string[]) => void;
  onRemoveKeys: (keys: string[]) => void;
  filter: string;
  showSelectedOnly: boolean;
  defaultExpanded: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const q = filter.trim().toLowerCase();
  const pagePerm = area.pagePerm;
  const actionGroups = area.actionGroups ?? [];
  const children = area.children ?? [];

  const pageSelected = pagePerm ? selected.has(pagePerm.key) : false;

  const allAreaKeys = featureAreaKeys(area);
  const selectedInArea = allAreaKeys.filter((k) => selected.has(k)).length;
  const allAreaSelected = allAreaKeys.length > 0 && selectedInArea === allAreaKeys.length;
  const someAreaSelected = selectedInArea > 0 && !allAreaSelected;

  const matchesPerm = (p: PermDef) => {
    if (showSelectedOnly && !selected.has(p.key)) return false;
    if (q && !`${p.name} ${p.description}`.toLowerCase().includes(q)) return false;
    return true;
  };

  const pageVisible = pagePerm ? matchesPerm(pagePerm) : false;
  const visibleGroups = actionGroups
    .map((g) => ({ ...g, visiblePerms: g.perms.filter(matchesPerm) }))
    .filter((g) => g.visiblePerms.length > 0);
  const visibleChildren = children.filter((child) => areaHasVisiblePerms(child, matchesPerm));

  if (!pageVisible && visibleGroups.length === 0 && visibleChildren.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {/* Area header */}
      <div className="flex w-full items-center gap-3 bg-muted/50 px-4 py-3 transition-colors hover:bg-muted/70">
        <Checkbox
          checked={allAreaSelected ? true : someAreaSelected ? "indeterminate" : false}
          onCheckedChange={() => onToggleGroup(allAreaKeys)}
          aria-label={t("iam.permissionEditor.toggleAllArea", { area: area.label })}
          className="shrink-0"
        />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex flex-1 items-center gap-3 text-left"
        >
          <span className="flex-1 text-sm font-semibold text-foreground">{area.label}</span>
          <span className="text-xs tabular-nums text-muted-foreground">
            {selectedInArea}/{allAreaKeys.length}
          </span>
          <ChevronDown
            className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", expanded && "rotate-180")}
          />
        </button>
      </div>

      {expanded && (
        <div className="divide-y divide-border border-t border-border">
          {/* Page access row */}
          {pageVisible && pagePerm && (
            <div className="bg-background px-4 py-3">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("iam.permissionEditor.pageAccess")}
              </p>
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={pageSelected}
                  onCheckedChange={() => {
                    if (pageSelected) {
                      // Cascade-remove the page perm AND every action perm in the
                      // area. Must be a direct delete — `onToggleGroup` would *add*
                      // any unselected action keys instead of removing them, which
                      // is the opposite of revoking page access.
                      const actionKeys = actionGroups.flatMap((g) => g.perms.map((p) => p.key));
                      onRemoveKeys([pagePerm.key, ...actionKeys]);
                    } else {
                      onTogglePerm(pagePerm.key);
                    }
                  }}
                  className="mt-0.5 shrink-0"
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-sm font-medium text-foreground">{pagePerm.name}</span>
                  <span className="text-xs text-muted-foreground">{pagePerm.description}</span>
                </div>
              </label>
            </div>
          )}

          {/* Action sub-groups */}
          {visibleGroups.map((group) => {
            const groupKeys = group.perms.map((p) => p.key);
            const allGroupSelected = groupKeys.every((k) => selected.has(k));
            const someGroupSelected = !allGroupSelected && groupKeys.some((k) => selected.has(k));
            const actionsDisabled = !pageSelected;

            return (
              <div key={group.id} className={cn("bg-background px-4 py-3", actionsDisabled && "opacity-50")}>
                <div className="mb-2 flex items-center gap-2">
                  <Checkbox
                    checked={allGroupSelected ? true : someGroupSelected ? "indeterminate" : false}
                    onCheckedChange={() => onToggleGroup(groupKeys)}
                    aria-label={t("iam.permissionEditor.toggleAllGroup", { group: group.label })}
                    className="shrink-0"
                    disabled={actionsDisabled}
                  />
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-y-1 pl-7 sm:grid-cols-2 sm:gap-x-4">
                  {group.visiblePerms.map((perm) => (
                    <label key={perm.key} className={cn("flex items-start gap-2 py-1.5", actionsDisabled ? "cursor-not-allowed" : "cursor-pointer")}>
                      <Checkbox
                        checked={selected.has(perm.key)}
                        onCheckedChange={() => onTogglePerm(perm.key)}
                        className="mt-0.5 shrink-0"
                        aria-label={perm.name}
                        disabled={actionsDisabled}
                      />
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="text-[13px] font-medium text-foreground leading-tight">{perm.name}</span>
                        <span className="text-[11px] text-muted-foreground leading-snug">{perm.description}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          {visibleChildren.length > 0 && (
            <div className="flex flex-col gap-2 bg-background p-3">
              {visibleChildren.map((child) => (
                <FeatureAreaSection
                  key={child.id}
                  area={child}
                  selected={selected}
                  onTogglePerm={onTogglePerm}
                  onToggleGroup={onToggleGroup}
                  onRemoveKeys={onRemoveKeys}
                  filter={filter}
                  showSelectedOnly={showSelectedOnly}
                  defaultExpanded={Boolean(q) || showSelectedOnly || defaultExpanded}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── NewRoleDialog ────────────────────────────────────────────────────────────

interface NewRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgs: OrgPickerItem[];
  defaultOrgId?: string | null;
  existingRoles?: Array<{ name: string; tenantId: string }>;
}

export function NewRoleDialog({ open, onOpenChange, orgs, defaultOrgId, existingRoles = [] }: NewRoleDialogProps) {
  const [name, setName] = useState("");
  const [orgIds, setOrgIds] = useState<string[]>(() => {
    if (defaultOrgId) return [defaultOrgId];
    return orgs[0]?.id ? [orgs[0].id] : [];
  });
  const [description, setDescription] = useState("");
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const { selected, setSelected, togglePerm, toggleGroup, removeKeys, selectedCount } = usePermissionSelection(
    new Set(), { onChange: () => setActivePreset(null) },
  );
  const [permFilter, setPermFilter] = useState("");
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const prevOpenRef = useRef(false);

  const { t } = useTranslation();
  const createAccessGroup = useCreateAccessGroup();
  const createAccessGroupsBulk = useCreateAccessGroupsBulk();
  const perms = usePermissions();
  const hasPermission = perms.IDENTITY_HUB_CREATE_ACCESS.isOn;

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;
    if (defaultOrgId) setOrgIds([defaultOrgId]);
    else if (orgs[0]?.id) setOrgIds([orgs[0].id]);
    else setOrgIds([]);
  }, [open, defaultOrgId, orgs]);

  const trimmedName = name.trim();

  const isDuplicate = trimmedName.length > 0 && orgIds.some((orgId) =>
    existingRoles.some(
      (r) => r.tenantId === orgId && r.name.toLowerCase() === trimmedName.toLowerCase(),
    ),
  );

  const applyPreset = (presetId: string) => {
    const preset = PRESET_DEFS.find((p) => p.id === presetId);
    if (!preset) return;
    setSelected(new Set(preset.keys));
    setActivePreset(presetId);
    setName(preset.label);
    setNameError(null);
  };

  const reset = () => {
    setName("");
    setOrgIds(defaultOrgId ? [defaultOrgId] : orgs[0]?.id ? [orgs[0].id] : []);
    setDescription("");
    setSelected(new Set());
    setActivePreset(null);
    setPermFilter("");
    setShowSelectedOnly(false);
    setNameError(null);
  };

  const handleOpenChange = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleCancel = () => handleOpenChange(false);

  const handleSubmit = async () => {
    if (!hasPermission || isDuplicate) return;
    setNameError(null);
    const permissions = permissionKeysToPayload(selected);
    try {
      if (orgIds.length === 1) {
        await createAccessGroup.mutateAsync({
          name: trimmedName,
          tenantId: orgIds[0]!,
          description: description.trim() || undefined,
          permissions,
        });
      } else {
        await createAccessGroupsBulk.mutateAsync({
          name: trimmedName,
          tenantIds: orgIds,
          description: description.trim() || undefined,
          permissions,
        });
      }
      handleCancel();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.toLowerCase().includes("already exists")) {
        setNameError(t("iam.validation.roleNameTaken"));
      }
    }
  };

  const nameValid = trimmedName.length > 0;
  const isCreating = createAccessGroup.isPending || createAccessGroupsBulk.isPending;
  const canSubmit = nameValid && !isDuplicate && !nameError && orgIds.length > 0 && hasPermission && !isCreating && description.length <= 500;

  const isFiltering = permFilter.trim().length > 0 || showSelectedOnly;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={ROLE_PERMISSION_DIALOG_CONTENT_CLASS}>
        {/* ── Header ─────────────────────────────────────────── */}
        <DialogIconHeader
          icon={Shield}
          title={t("iam.newRoleDialog.title")}
          description={t("iam.newRoleDialog.description")}
        />

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5">
          {/* Name + Org row */}
          <div className="grid grid-cols-1 gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                {t("iam.newRoleDialog.roleNameLabel")} <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                placeholder={t("iam.newRoleDialog.roleNamePlaceholder")}
                value={name}
                onChange={(e) => { setName(e.target.value); setNameError(null); }}
                className={cn(
                  "h-9 rounded-md border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1",
                  isDuplicate || nameError
                    ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                    : "border-border focus:border-ring focus:ring-ring/40",
                )}
                autoFocus
              />
              {(isDuplicate || nameError) && (
                <p className="text-[11px] text-destructive">
                  {nameError ?? t("iam.validation.roleNameTaken")}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-foreground">
                {t("iam.newRoleDialog.organizationsLabel")} <span className="text-destructive">*</span>
              </label>
              <OrgMultiPicker
                items={orgs}
                value={orgIds}
                onValueChange={setOrgIds}
                placeholder={t("iam.personDrawer.orgs.addOrgs", "Add organizations…")}
                searchPlaceholder={t("iam.personDrawer.orgs.searchOrgs")}
                ariaLabel={t("iam.personDrawer.orgs.organizations", "Organizations")}
              />
            </div>
          </div>

          {/* Description */}
          {(() => {
            const DESC_MAX = 500;
            const descOver = description.length > DESC_MAX;
            return (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between">
                  <label className="text-xs font-medium text-foreground">{t("iam.newRoleDialog.descriptionLabel")}</label>
                  <span className={cn("text-[11px] tabular-nums", descOver ? "text-destructive" : "text-muted-foreground/60")}>
                    {description.length > 0 ? `${description.length}/${DESC_MAX}` : t("iam.newRoleDialog.descriptionOptional")}
                  </span>
                </div>
                <textarea
                  placeholder={t("iam.newRoleDialog.descriptionPlaceholder")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className={cn(
                    "resize-none rounded-md border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:ring-1",
                    descOver
                      ? "border-destructive focus:border-destructive focus:ring-destructive/40"
                      : "border-border focus:border-ring focus:ring-ring/40",
                  )}
                />
                {descOver && (
                  <p className="text-[11px] text-destructive">
                    {t("iam.newRoleDialog.descriptionTooLong", { max: DESC_MAX })}
                  </p>
                )}
              </div>
            );
          })()}

          {/* Presets */}
          <div className="flex flex-col gap-2">
            <p className="text-xs font-medium text-foreground">{t("iam.newRoleDialog.templateSectionTitle")}</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {PRESET_DEFS.map((preset) => {
                const isActive = activePreset === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset.id)}
                    className={cn(
                      "flex flex-col gap-1 rounded-lg border px-3 py-2.5 text-left transition-all",
                      isActive
                        ? "border-primary/50 bg-primary/5 ring-1 ring-primary/30"
                        : "border-border bg-background hover:border-foreground/20 hover:bg-muted/30",
                    )}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-[13px] font-semibold text-foreground">{preset.label}</span>
                      {isActive && <Check className="h-3 w-3 shrink-0 text-primary" />}
                    </div>
                    <span className="text-[11px] leading-snug text-muted-foreground">{preset.description}</span>
                    <span className="text-[11px] tabular-nums text-muted-foreground/70">
                      {t("iam.newRoleDialog.presetPermissionCount", { count: preset.keys.length })}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Permissions section */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="min-w-32 flex-1 text-xs font-medium text-foreground">
                {t("iam.permissionEditor.permissionsLabel")}
                {selectedCount > 0 && (
                  <span className="ml-1.5 tabular-nums text-muted-foreground">
                    ({selectedCount}/{TOTAL_PERM_COUNT})
                  </span>
                )}
              </span>
              <label className="relative flex h-8 w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/40 sm:w-64 lg:w-72">
                <Search className="h-3.5 w-3.5 shrink-0" />
                <input
                  type="search"
                  placeholder={t("iam.permissionEditor.filterPlaceholder")}
                  value={permFilter}
                  onChange={(e) => setPermFilter(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/70"
                />
              </label>
              <button
                type="button"
                onClick={() => setShowSelectedOnly((v) => !v)}
                className={cn(
                  "flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition-colors",
                  showSelectedOnly
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground",
                )}
              >
                <Check className="h-3 w-3" />
                {t("iam.permissionEditor.selectedFilter")}
              </button>
            </div>

            <div className="flex flex-col gap-2">
              {FEATURE_AREAS.map((area, i) => (
                <FeatureAreaSection
                  key={area.id}
                  area={area}
                  selected={selected}
                  onTogglePerm={togglePerm}
                  onToggleGroup={toggleGroup}
                  onRemoveKeys={removeKeys}
                  filter={permFilter}
                  showSelectedOnly={showSelectedOnly}
                  defaultExpanded={isFiltering || i === 0}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────── */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <span className="text-xs text-muted-foreground">
            <strong className="text-foreground">{selectedCount}</strong> {t("iam.permissionEditor.footerOf")}{" "}
            <strong className="text-foreground">{TOTAL_PERM_COUNT}</strong> {t("iam.permissionEditor.footerPermissionsSelected")}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCancel}>
              {t("common.cancel")}
            </Button>
            <Button size="sm" className="gap-1.5" disabled={!canSubmit} onClick={() => void handleSubmit()}>
              <Shield className="h-3.5 w-3.5" />
              {isCreating
                ? t("iam.newRoleDialog.creating")
                : orgIds.length > 1
                  ? t("iam.newRoleDialog.createRoleInOrgs", { count: orgIds.length })
                  : t("iam.newRoleDialog.createRole")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
